import { useCallback, useEffect, useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import postsApi from '@repo/editor/src/api/posts';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { TEMPLATES_PATH } from './templateParams';

/**
 * This constant is a list of pages that show search results managed by searchStore.
 * With this information we can avoid redirecting or forcing a refresh if search query changes,
 * and we're on any of these paths.
 * Index page is a templates page only for logged-in users
 */
const RESULTS_PAGES: { path: string; forLoggedOut: boolean }[] = [
  { path: '/', forLoggedOut: false },
  {
    path: `${TEMPLATES_PATH}/[[...useCaseSlug]]`,
    forLoggedOut: true,
  },
];

interface State {
  query: string;
  commitQuery: boolean;
  tags: string[];
  initializedSuggestions: boolean;
  setQuery: ({ text, commit }: { text: string; commit: boolean }) => void;
  loadSuggestions: () => Promise<void>;
}

const useSearchStore = create<State>((set, get) => ({
  query: '',
  commitQuery: false,
  tags: [],

  setQuery: ({ text, commit }): void => {
    set({
      query: text,
      commitQuery: commit,
    });
  },

  // init suggestions
  initializedSuggestions: false,
  loadSuggestions: async (): Promise<void> => {
    if (get().initializedSuggestions) {
      return;
    }
    set({ initializedSuggestions: true });

    const response = await postsApi.getTemplateSuggestions();
    if ('error' in response) {
      return;
    }

    const { names, authors, tags, useCases } = response;

    set({
      tags: [...names, ...authors, ...tags, ...useCases],
    });
  },
}));

export interface UseSearchResult {
  onSearch: (newQuery: string) => void;
  resetQuery: (skipRouter?: boolean) => void;
  tags: string[];
  query: string;
}

const useSearch = (): UseSearchResult => {
  const router = useRouter();
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(
    null
  );
  const pathname = usePathname();
  const isLoggedIn = useUserStore(userStoreSelector.isLoggedIn);
  const { query, tags, setQuery } = useStoreWithEqualityFn(
    useSearchStore,
    (state) => ({
      query: state.query,
      tags: state.tags,
      setQuery: state.setQuery,
    }),
    shallow
  );

  useEffect(() => {
    setSearchParams(new URLSearchParams(window.location.search));
    void useSearchStore.getState().loadSuggestions();
  }, []);

  const queryParam = searchParams?.get('query');

  useEffect(() => {
    if (queryParam) {
      setQuery({ text: queryParam || '', commit: true });
    }
  }, [queryParam, setQuery]);

  // skipRouter should be used when the action calling resetQuery also uses the router to go somewhere
  const resetQuery = useCallback(
    (skipRouter?: boolean): void => {
      setQuery({ text: '', commit: true });

      if (
        !skipRouter &&
        !RESULTS_PAGES.some(
          (x) => x.path === pathname && (isLoggedIn || x.forLoggedOut)
        )
      ) {
        const queryParams = new URLSearchParams(searchParams?.toString());
        queryParams.delete('query');
        const allQueryParams = queryParams?.toString();
        const newUrl = `${pathname}${allQueryParams ? '?' + allQueryParams : ''}`;

        void router.push(newUrl ?? '', { scroll: false });
      }
    },
    [setQuery, pathname, isLoggedIn, searchParams, router]
  );

  const onSearch = useCallback(
    (newQuery: string): void => {
      setQuery({ text: newQuery, commit: true });

      if (
        !RESULTS_PAGES.some(
          (x) => x.path === pathname && (isLoggedIn || x.forLoggedOut)
        )
      ) {
        const queryParams = new URLSearchParams(searchParams?.toString());
        newQuery
          ? queryParams.set('query', newQuery)
          : queryParams.delete('query');

        const newPathName = pathname?.includes(TEMPLATES_PATH)
          ? pathname
          : TEMPLATES_PATH;

        const allQueryParams = queryParams?.toString();
        const newUrl = `${newPathName}${allQueryParams ? '?' + allQueryParams : ''}`;

        void router.push(newUrl, {
          scroll: false,
        });
      }
    },
    [setQuery, pathname, isLoggedIn, searchParams, router]
  );

  return {
    onSearch,
    resetQuery,
    tags,
    query: query,
  };
};

export default useSearch;
